import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Shared Panel Animation Config ───────────────────────────────────────────
const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 6, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 4, scale: 0.97 },
};
const PANEL_TRANSITION = { duration: 0.15, ease: [0.16, 1, 0.3, 1] };

/**
 * PageHeader
 * A unified header pattern component that manages layout, breadcrumbs, titles, and active popovers.
 */

export function PageHeader({ children, className = '', isSticky = true }) {
    const [activePanel, setActivePanel] = useState(null);
    const headerRef = useRef(null);

    // Close panels on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (headerRef.current && !headerRef.current.contains(event.target)) {
                setActivePanel(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const togglePanel = (panelId) => {
        setActivePanel(activePanel === panelId ? null : panelId);
    };

    return (
        <div 
            ref={headerRef} 
            className={`w-full flex flex-col bg-[var(--bg-surface)] border-b border-[var(--border)] z-[var(--z-sticky)] ${isSticky ? 'sticky top-0' : ''} ${className}`}
        >
            {/* Provide activePanel context to children implicitly using React.Children.map if we wanted to, 
                but a context provider is cleaner for compound components. */}
            <PageHeaderContext.Provider value={{ activePanel, setActivePanel, togglePanel }}>
                {children}
            </PageHeaderContext.Provider>
        </div>
    );
}

const PageHeaderContext = React.createContext({
    activePanel: null,
    setActivePanel: () => {},
    togglePanel: () => {}
});

export function PageHeaderTopRow({ children, className = '' }) {
    return (
        <div className={`flex items-center justify-between px-5 pt-3 pb-0 text-[12px] text-[var(--text-tertiary)] font-medium ${className}`}>
            {children}
        </div>
    );
}

export function PageHeaderMainRow({ children, className = '' }) {
    return (
        <div className={`flex justify-between items-center px-5 py-3 ${className}`}>
            {children}
        </div>
    );
}

export function PageHeaderTitle({ title, children, className = '' }) {
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <h1 className="text-2xl font-medium text-[var(--text-primary)] tracking-tight m-0">
                {title}
            </h1>
            {children}
        </div>
    );
}

export function PageHeaderActions({ children, className = '' }) {
    return (
        <div className={`flex items-center gap-0.5 ${className}`}>
            {children}
        </div>
    );
}

export function PageHeaderActionItem({ 
    id, 
    icon, 
    activeIconColor = 'var(--text-primary)', 
    activeBgColor = 'var(--bg-subtle)',
    popoverContent, 
    popoverWidth = 'auto',
    onClick,
    isActiveOverride, // if provided, overrides internal panel state
    className = '' 
}) {
    const buttonRef = useRef(null);
    const { activePanel, togglePanel, setActivePanel } = React.useContext(PageHeaderContext);
    
    const isActive = isActiveOverride !== undefined ? isActiveOverride : activePanel === id;

    const handleClick = (e) => {
        if (onClick) onClick(e);
        if (popoverContent) {
            togglePanel(id);
        }
    };

    return (
        <div className="relative" ref={buttonRef}>
            <button
                onClick={handleClick}
                className={`h-8 w-8 flex items-center justify-center bg-transparent rounded-md border-none cursor-pointer transition-colors ${className}`}
                style={{ 
                    color: isActive ? activeIconColor : 'var(--text-tertiary)',
                    background: isActive ? activeBgColor : 'transparent'
                }}
            >
                <ObliqueIcon name={icon} size={20} />
            </button>

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {isActive && popoverContent && (
                        <motion.div
                            variants={PANEL_VARIANTS}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={PANEL_TRANSITION}
                            style={{ 
                                position: 'fixed',
                                top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 8 : 0,
                                left: buttonRef.current ? (buttonRef.current.getBoundingClientRect().right - (popoverWidth === 'auto' ? 320 : parseInt(popoverWidth))) : 0,
                                width: popoverWidth === 'auto' ? 'max-content' : popoverWidth, 
                                minWidth: '200px',
                                zIndex: 'var(--z-popover, 2000)'
                            }}
                            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl overflow-hidden origin-top-right"
                        >
                            {typeof popoverContent === 'function' ? popoverContent({ close: () => setActivePanel(null) }) : popoverContent}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
}

export function PageHeaderPrimaryButton({ children, onClick, icon = 'keyboard_arrow_down', onIconClick, className = '' }) {
    return (
        <div className={`flex ml-2 ${className}`}>
            <button 
                onClick={onClick}
                className="h-8 px-3 bg-[var(--accent)] text-white border-none rounded-l-md text-[13px] font-medium cursor-pointer hover:brightness-110 transition-all"
            >
                {children}
            </button>
            <button 
                onClick={onIconClick}
                className="h-8 w-7 bg-[var(--accent)] text-white border-none border-l border-white/25 rounded-r-md cursor-pointer flex items-center justify-center hover:brightness-110 transition-all"
            >
                <ObliqueIcon name={icon} size={16} />
            </button>
        </div>
    );
}
